/* Presentation enhancement for the original campaign only.
   No copy, product data, cart requests or variant logic are replaced here. */
(() => {
  if (!document.body.classList.contains('template-product-campaign-lab')) return;
  const cleanups = new Map();

  const enhance = (section) => {
    if (cleanups.has(section)) return;
    const form = section.querySelector('.product-form[data-add-to-cart]');
    const primary = form?.querySelector('[data-add-button]');
    const buybar = section.querySelector('[data-mobile-buybar]');
    const shortcut = buybar?.querySelector('[data-submit-product-form]');
    const quantity = form?.querySelector('[data-product-quantity]');
    if (!primary || !buybar || !shortcut || !quantity) return;
    const desktop = window.matchMedia('(min-width: 990px)');
    let busy = false;
    let shortcutWasDisabled = shortcut.disabled;
    let refreshFrame = 0;

    const syncVisibility = () => {
      const box = primary.getBoundingClientRect();
      // Keep the mobile CTA: the original inline control is hidden below 990px.
      // At desktop, reveal the duplicate only after the inline CTA leaves above.
      const hide = desktop.matches && box.bottom > 0 && box.width > 0;
      // Do not remove a control while someone is using it with a keyboard.
      const shouldHide = hide && !buybar.contains(document.activeElement);
      buybar.toggleAttribute('data-campaign-buybar-hidden', shouldHide);
      buybar.inert = shouldHide;
    };

    const syncQuantityControls = () => {
      const value = Number(quantity.value);
      if (!Number.isFinite(value)) return;
      const min = Number(quantity.min || 1);
      const max = quantity.max ? Number(quantity.max) : Infinity;
      const unavailable = primary.disabled && !busy;
      section.querySelectorAll('[data-quantity-minus]').forEach((button) => {
        button.disabled = busy || unavailable || value <= min;
      });
      section.querySelectorAll('[data-quantity-plus]').forEach((button) => {
        button.disabled = busy || unavailable || value >= max;
      });
    };

    const finishBusy = () => {
      busy = false;
      primary.removeAttribute('aria-busy');
      shortcut.removeAttribute('aria-busy');
      shortcut.disabled = shortcutWasDisabled;
      syncQuantityControls();
    };
    const scheduleRefresh = () => {
      if (refreshFrame) window.cancelAnimationFrame(refreshFrame);
      // Native event dispatch may checkpoint microtasks between listeners.
      // Wait for the theme's delegated document handlers before reading state.
      refreshFrame = window.requestAnimationFrame(() => {
        refreshFrame = 0;
        if (busy && !primary.disabled) finishBusy();
        syncQuantityControls();
        if (intersection) syncVisibility();
      });
    };
    const onSubmit = (event) => {
      if (event.target !== form) return;
      if (busy) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      busy = true;
      shortcutWasDisabled = shortcut.disabled;
      primary.setAttribute('aria-busy', 'true');
      shortcut.setAttribute('aria-busy', 'true');
      shortcut.disabled = true;
      syncQuantityControls();
      // The existing theme owns submission and clears its disabled flag in finally.
      scheduleRefresh();
    };
    const onControlChange = (event) => {
      if (event.target.closest('[data-quantity-minus], [data-quantity-plus], [data-product-quantity], [data-product-option]')) {
        scheduleRefresh();
      }
    };
    const onFocusOut = scheduleRefresh;
    const observer = new MutationObserver(() => {
      if (busy && !primary.disabled) finishBusy();
      else syncQuantityControls();
    });
    observer.observe(primary, { attributes: true, attributeFilter: ['disabled'] });
    observer.observe(quantity, { attributes: true, attributeFilter: ['min', 'max', 'disabled'] });

    let intersection;
    if ('IntersectionObserver' in window) {
      intersection = new IntersectionObserver(syncVisibility, { threshold: [0, 1] });
      intersection.observe(primary);
      syncVisibility();
      desktop.addEventListener('change', syncVisibility);
      buybar.addEventListener('focusout', onFocusOut);
    }
    form.addEventListener('submit', onSubmit, true);
    section.addEventListener('click', onControlChange);
    section.addEventListener('input', onControlChange);
    section.addEventListener('change', onControlChange);
    syncQuantityControls();

    cleanups.set(section, () => {
      if (refreshFrame) window.cancelAnimationFrame(refreshFrame);
      intersection?.disconnect();
      observer.disconnect();
      desktop.removeEventListener('change', syncVisibility);
      buybar.removeEventListener('focusout', onFocusOut);
      form.removeEventListener('submit', onSubmit, true);
      section.removeEventListener('click', onControlChange);
      section.removeEventListener('input', onControlChange);
      section.removeEventListener('change', onControlChange);
      buybar.removeAttribute('data-campaign-buybar-hidden');
      buybar.inert = false;
      if (busy) finishBusy();
      cleanups.delete(section);
    });
  };

  const init = () => document.querySelectorAll('[data-product-section]').forEach(enhance);
  init();
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    cleanups.forEach((cleanup, section) => { if (event.target.contains(section)) cleanup(); });
  });
  window.addEventListener('pagehide', () => cleanups.forEach((cleanup) => cleanup()));
  window.addEventListener('pageshow', (event) => { if (event.persisted) init(); });
})();
